import { format } from "date-fns";
import generatePDF from "helpers/generatePDF";
import type { Content, TableCell } from "pdfmake/interfaces";
import type { FinanceiroLojaFilters } from "hooks/useFinanceiroLoja";
import type {
  ILancamentoFinanceiroRow,
  IFinanceiroResumoResponse,
} from "interfaces/loja/financeiro";
import { formatarDataMovimentacaoFinanceiro } from "../dashboard/helpers/formatar-data-financeiro";

const brl = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

function labelTipo(t: string) {
  if (t === "RECEITA") return "Receita";
  if (t === "DESPESA") return "Despesa";
  return t;
}

function labelOrigem(o: string) {
  const map: Record<string, string> = {
    PEDIDO: "Pedido (legado)",
    VENDA: "Venda",
    FRETE: "Frete",
    TAXA_GATEWAY: "Taxa gateway",
    ESTORNO: "Estorno",
    AJUSTE: "Ajuste",
  };
  return map[o] ?? o;
}

function labelStatus(s: string) {
  const map: Record<string, string> = {
    PENDENTE: "Pendente",
    CONFIRMADO: "Confirmado",
    CANCELADO: "Cancelado",
  };
  return map[s] ?? s;
}

function filtrosTexto(f: FinanceiroLojaFilters): string {
  const parts: string[] = [];
  if (f.dataInicio) parts.push(`De ${f.dataInicio}`);
  if (f.dataFim) parts.push(`Até ${f.dataFim}`);
  if (f.tipo) parts.push(`Tipo: ${labelTipo(f.tipo)}`);
  if (f.origem) parts.push(`Origem: ${labelOrigem(f.origem)}`);
  if (f.status) parts.push(`Status: ${labelStatus(f.status)}`);
  if (f.pedidoId.trim()) parts.push(`Pedido: ${f.pedidoId.trim()}`);
  if (f.pagamentoId.trim()) parts.push(`Pagamento: ${f.pagamentoId.trim()}`);
  return parts.length > 0
    ? parts.join(" · ")
    : "Nenhum filtro de período ou dimensão (resumo conforme API; listagem paginada).";
}

function fmtData(iso: string) {
  return formatarDataMovimentacaoFinanceiro(iso);
}

export async function printFinanceiroLojaPdf(input: {
  resumo: IFinanceiroResumoResponse | null;
  rows: ILancamentoFinanceiroRow[];
  appliedFilters: FinanceiroLojaFilters;
  page: number;
  pageSize: number;
  total: number;
}) {
  const { resumo, rows, appliedFilters, page, pageSize, total } = input;
  const agora = formatarDataMovimentacaoFinanceiro(new Date().toISOString());
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const headerRows: TableCell[][] = [
    [
      { text: "Competência", bold: true, fontSize: 8 },
      { text: "Caixa", bold: true, fontSize: 8 },
      { text: "Tipo", bold: true, fontSize: 8 },
      { text: "Origem", bold: true, fontSize: 8 },
      { text: "Status", bold: true, fontSize: 8 },
      { text: "Valor", bold: true, fontSize: 8 },
      { text: "Descrição", bold: true, fontSize: 8 },
    ],
  ];

  const bodyRows: TableCell[][] = rows.map((r) => [
    fmtData(r.dataCompetencia ?? r.dataEfetiva),
    fmtData(r.dataEfetiva),
    labelTipo(r.tipo),
    labelOrigem(r.origem),
    labelStatus(r.status),
    { text: brl.format(Number(r.valor)), alignment: "right" },
    (r.descricao ?? "—").slice(0, 80),
  ]);

  const emptyRow: TableCell[] = [
    {
      text: "Nenhum lançamento nesta página.",
      colSpan: 7,
      alignment: "center",
      fontSize: 9,
      color: "#666666",
    },
    {},
    {},
    {},
    {},
    {},
    {},
  ];

  const tableBody: TableCell[][] =
    rows.length === 0 ? [...headerRows, emptyRow] : [...headerRows, ...bodyRows];

  const content: Content[] = [
    {
      text: "Financeiro da loja",
      fontSize: 18,
      bold: true,
      margin: [0, 0, 0, 4],
    },
    {
      text: `Emitido em ${agora}`,
      fontSize: 9,
      color: "#666666",
      margin: [0, 0, 0, 8],
    },
    {
      text: `Filtros: ${filtrosTexto(appliedFilters)}`,
      fontSize: 9,
      margin: [0, 0, 0, 12],
    },
    {
      columns: [
        {
          width: "*",
          stack: [
            { text: "Total receitas", fontSize: 8, color: "#666666" },
            {
              text: brl.format(resumo?.totalReceitas ?? 0),
              fontSize: 12,
              bold: true,
              color: "#2e7d32",
            },
          ],
        },
        {
          width: "*",
          stack: [
            { text: "Total despesas", fontSize: 8, color: "#666666" },
            {
              text: brl.format(resumo?.totalDespesas ?? 0),
              fontSize: 12,
              bold: true,
              color: "#c62828",
            },
          ],
        },
        {
          width: "*",
          stack: [
            { text: "Saldo", fontSize: 8, color: "#666666" },
            {
              text: brl.format(resumo?.saldo ?? 0),
              fontSize: 12,
              bold: true,
              color: "#1565c0",
            },
          ],
        },
      ],
      margin: [0, 0, 0, 16],
    },
    {
      text: `Lançamentos (página ${page} de ${totalPages} — ${total} registro(s) no total, ${rows.length} nesta página)`,
      fontSize: 11,
      bold: true,
      margin: [0, 0, 0, 8],
    },
    {
      table: {
        headerRows: 1,
        widths: [62, 62, 48, 56, 52, 56, "*"],
        body: tableBody,
      },
      layout: {
        fillColor: (rowIndex: number) =>
          rowIndex === 0 ? "#eeeeee" : rowIndex % 2 === 0 ? "#f9f9f9" : null,
      },
    },
  ];

  await generatePDF({
    filename: `financeiro-loja-${format(new Date(), "yyyy-MM-dd-HHmm")}`,
    content,
    header: {
      text: "TNV Team Sports — Loja",
      alignment: "center",
      margin: [0, 16, 0, 0],
      fontSize: 9,
      color: "#666666",
    },
    footer: (currentPage, pageCount) => ({
      text: `Página ${currentPage} de ${pageCount}`,
      alignment: "center",
      fontSize: 8,
      color: "#888888",
      margin: [0, 8, 0, 0],
    }),
  });
}
